import type { Store } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';
import { useCallback, useMemo, useRef } from 'react';
import { useManagementPageState } from './useManagementPageState';
import {
  type QueryLoadContext,
  usePaginatedQueryLoader,
} from './usePaginatedQueryLoader';

interface ManagementStoreState<TFilters> {
  filters: TFilters;
  sorting: SortingState;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
  ui: {
    isLoading: boolean;
  };
}

interface ManagementCrudActions<TState extends ManagementStoreState<unknown>> {
  updateFilter: <K extends keyof TState['filters']>(
    key: K,
    value: TState['filters'][K],
  ) => void;
  setSorting: (
    sorting: SortingState | ((currentSorting: SortingState) => SortingState),
  ) => void;
  setPage: (page: number) => void;
  setPaginationInfo: (
    currentPage: number,
    totalPages: number,
    total: number,
  ) => void;
  setLoading: (isLoading: boolean) => void;
}

interface UseManagementCrudOptions<
  TState extends ManagementStoreState<unknown>,
> {
  enabled: boolean;
  store: Store<TState>;
  actions: ManagementCrudActions<TState>;
  loadPage: (
    context: QueryLoadContext & {
      filters: TState['filters'];
      sorting: SortingState;
      pagination: TState['pagination'];
    },
  ) => Promise<void>;
}

export function useManagementCrud<
  TState extends ManagementStoreState<unknown>,
>({ enabled, store, actions, loadPage }: UseManagementCrudOptions<TState>) {
  const { filters, sorting, pagination, isLoading } =
    useManagementPageState(store);

  const latestQueryStateRef = useRef({
    filters,
    sorting,
    pagination,
  });
  const loadPageRef = useRef(loadPage);

  latestQueryStateRef.current = {
    filters,
    sorting,
    pagination,
  };
  loadPageRef.current = loadPage;

  const queryStateKey = useMemo(
    () =>
      JSON.stringify({
        filters,
        sorting,
        currentPage: pagination.currentPage,
      }),
    [filters, pagination.currentPage, sorting],
  );

  const loadCurrentPage = useCallback((context: QueryLoadContext) => {
    const currentQueryState = latestQueryStateRef.current;

    return loadPageRef.current({
      ...context,
      filters: currentQueryState.filters,
      sorting: currentQueryState.sorting,
      pagination: currentQueryState.pagination,
    });
  }, []);

  const { reload } = usePaginatedQueryLoader({
    enabled,
    setLoading: actions.setLoading,
    loadPage: loadCurrentPage,
    reloadKey: queryStateKey,
  });

  return {
    filters,
    sorting,
    pagination,
    isLoading,
    reload,
    updateFilter: actions.updateFilter,
    setSorting: actions.setSorting,
    setPage: actions.setPage,
    setPaginationInfo: actions.setPaginationInfo,
    setLoading: actions.setLoading,
  };
}
